import { useEffect, useState } from "react";
import { useToasts } from "./Toasts";

/** Whether a newer WOV is deployed than the one this page runs: the
 *  notice below and the rail's Reload button (its dot) both read it. */
let updateAvailable = false;
const updateListeners = new Set<(v: boolean) => void>();
function setUpdateAvailable(v: boolean): void {
  if (updateAvailable === v) return;
  updateAvailable = v;
  for (const l of Array.from(updateListeners)) l(v);
}
export function useUpdateAvailable(): boolean {
  const [v, setV] = useState(updateAvailable);
  useEffect(() => {
    updateListeners.add(setV);
    setV(updateAvailable);
    return () => {
      updateListeners.delete(setV);
    };
  }, []);
  return v;
}

/** The build this page is running: its entry script, as Vite names it
 *  (/assets/index-<hash>.js). Null in development, where there is none. */
function runningBuild(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  const src = el?.getAttribute("src") ?? "";
  return /\/assets\/index-[\w-]+\.js/.exec(src)?.[0] ?? null;
}

const CHECK_EVERY_MS = 5 * 60_000;

/** Says when a newer WOV has been deployed. A window left open (the Mac app
 *  above all) keeps running the code it loaded, so fixes did not show until
 *  someone thought to reload. The page asks for the current entry script
 *  every few minutes and when the window comes forward; when it changed, a
 *  notice offers Reload. It never reloads by itself: that would drop Triage
 *  or a half-written message. */
export function useAppUpdateNotice(): void {
  const { toast } = useToasts();
  useEffect(() => {
    const running = runningBuild();
    if (!running) return;
    // When the notice was last shown: again after half an hour if this page
    // is still the old one (other notices can push it off the screen).
    let toldAt = 0;
    let busy = false;
    const check = async () => {
      if (busy || Date.now() - toldAt < 30 * 60_000) return;
      busy = true;
      try {
        const r = await fetch("/", { cache: "no-store", credentials: "same-origin" });
        const latest = /\/assets\/index-[\w-]+\.js/.exec(await r.text())?.[0];
        if (latest && latest !== running) {
          setUpdateAvailable(true);
          toldAt = Date.now();
          toast({
            text: "WOV was updated.",
            tone: "info",
            ttlMs: 24 * 60 * 60_000,
            action: { label: "Reload", run: () => window.location.reload() },
          });
        }
      } catch {
        /* offline: ask again later */
      } finally {
        busy = false;
      }
    };
    const onForward = () => {
      if (document.visibilityState === "visible") void check();
    };
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onForward);
    window.addEventListener("focus", onForward);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onForward);
      window.removeEventListener("focus", onForward);
    };
  }, [toast]);
}

/** Mounts the check; renders nothing. */
export function AppUpdateNotice(): null {
  useAppUpdateNotice();
  return null;
}
